import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Tabs, Tag, Space, message,
  Popconfirm, Upload, Typography, Badge, Tooltip, Statistic, Row, Col, Checkbox
} from 'antd';
import {
  PlusOutlined, UploadOutlined, YoutubeOutlined, EditOutlined, DeleteOutlined,
  CheckCircleOutlined, RollbackOutlined, LinkOutlined, BellOutlined, SearchOutlined, EyeOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api, { formatApiError } from '../utils/api';
import VtSongPicker from '../components/VtSongPicker';
import VtChannelField from '../components/VtChannelField';

const { Text, Title } = Typography;

export default function VideoTracker({ user }) {
  const isAdmin = ['admin', 'super_admin', 'manager', 'hr_manager'].includes(user?.role);
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pending: 0, uploaded: 0, total: 0 });
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState(null);

  const [addModal, setAddModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [uploadRow, setUploadRow] = useState(null);
  const [editUploadRow, setEditUploadRow] = useState(null);
  const [notifyModal, setNotifyModal] = useState(false);
  const [staff, setStaff] = useState([]);
  const [ftSelected, setFtSelected] = useState([]);
  const [ftAddModal, setFtAddModal] = useState(false);

  const [ftAddForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [editUploadForm] = Form.useForm();
  const [notifyForm] = Form.useForm();
  const searchTimer = useRef(null);
  const [showRow, setShowRow] = useState(null);
  const [copyRow, setCopyRow] = useState(null);
  const [cmsList, setCmsList] = useState([]);
  const [cmsRow, setCmsRow] = useState(null);
  const [cmsData, setCmsData] = useState(null);
  const [copyData, setCopyData] = useState(null);
  const [showStaff, setShowStaff] = useState([]);
  const [showTargets, setShowTargets] = useState([]);
  const [showPicked, setShowPicked] = useState([]);
  const [showSaving, setShowSaving] = useState(false);

  const openFtSheet = async () => {
    try {
      const res = await api.get('/video-tracker/sheet-link');
      if (res.data?.url) window.open(res.data.url, '_blank', 'noopener');
      else message.info('Sheet abhi ban rahi hai, 1 minute baad try karo');
    } catch (err) {
      message.error(formatApiError(err, 'Sheet link nahi mila'));
    }
  };

  const openShowStaff = async (row) => {
    setShowRow(row);
    setShowPicked([]);
    setShowTargets([]);
    try {
      const [st, tg] = await Promise.all([
        api.get('/video-tracker/publish-staff'),
        api.get(`/video-tracker/${row.id}/publish-targets`),
      ]);
      setShowStaff(st.data || []);
      const t = tg.data || [];
      setShowTargets(t);
      const active = t.filter(x => Number(x.hidden) === 0).map(x => Number(x.user_id));
      let last = [];
      try { last = JSON.parse(localStorage.getItem('vt_show_staff_last') || '[]'); } catch (e) { last = []; }
      setShowPicked(t.length ? active : last.filter(id => (st.data || []).some(u => Number(u.id) === id)));
    } catch (err) {
      message.error(formatApiError(err, 'Staff list load nahi hui'));
    }
  };

  const saveShowStaff = async () => {
    setShowSaving(true);
    try {
      await api.post(`/video-tracker/${showRow.id}/show-staff`, { user_ids: showPicked });
      localStorage.setItem('vt_show_staff_last', JSON.stringify(showPicked));
      message.success(showPicked.length ? `${showPicked.length} staff ko dikhaya` : 'Staff se chhupa diya');
      setShowRow(null);
      load();
    } catch (err) {
      message.error(formatApiError(err, 'Save nahi hua'));
    } finally { setShowSaving(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === 'firsttime') params.view = 'firsttime';
      else if (tab === 'list') params.view = 'list';
      else params.status = tab === 'uploaded' ? 'uploaded' : 'pending';
      if (search) params.search = search;
      if (channelFilter) params.channel_id = channelFilter;
      const [listRes, statsRes] = await Promise.all([
        api.get('/video-tracker', { params }),
        api.get('/video-tracker/stats'),
      ]);
      setRows(listRes.data || []);
      setStats(statsRes.data || { pending: 0, uploaded: 0, total: 0 });
    } catch (err) {
      message.error(formatApiError(err, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [tab, search, channelFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/channels').then(r => setChannels(r.data || [])).catch(() => {});
  }, []);

  // ---- Excel import ----
  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (!data.length) { message.error('Excel is empty'); return; }
        // Detect header row (first row that has "song")
        let headerIdx = 0;
        for (let i = 0; i < Math.min(3, data.length); i++) {
          if (data[i].some(c => String(c).toLowerCase().includes('song'))) { headerIdx = i; break; }
        }
        const rowsOut = [];
        for (let i = headerIdx + 1; i < data.length; i++) {
          const r = data[i];
          const title = String(r[0] || '').trim();
          if (!title) continue;
          rowsOut.push({
            song_title: title,
            artist: String(r[1] || '').trim(),
            singer: String(r[2] || '').trim(),
            video_shoot: String(r[3] || '').trim(),
            dress_colour: String(r[4] || '').trim(),
            note: String(r[5] || '').trim(),
          });
        }
        if (!rowsOut.length) { message.error('No song rows found in Excel'); return; }
        const res = await api.post('/video-tracker/import', { rows: rowsOut });
        message.success(`Imported ${res.data.added} songs (${res.data.skipped} skipped as duplicate)`);
        load();
      } catch (err) {
        message.error(formatApiError(err, 'Import failed'));
      }
    };
    reader.readAsBinaryString(file);
    return false; // prevent default upload
  };

  // ---- Import historical First-Time-Use data (Song/Singer/Month/Video Shoot/
  //      Video Edit/Channel/Video Link/Artist/Location/Dress) ----
  const findCol = (headers, ...needles) => {
    for (let i = 0; i < headers.length; i++) {
      const h = String(headers[i] || '').toLowerCase().trim();
      if (needles.some(n => h.includes(n))) return i;
    }
    return -1;
  };

  const handleImportFirstTime = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (!data.length) { message.error('Excel is empty'); return; }
        let headerIdx = 0;
        for (let i = 0; i < Math.min(4, data.length); i++) {
          if (data[i].some(c => String(c).toLowerCase().includes('song'))) { headerIdx = i; break; }
        }
        const H = data[headerIdx];
        const cSong = findCol(H, 'song', 'title');
        const cSinger = findCol(H, 'singer');
        const cMonth = findCol(H, 'month');
        const cShoot = findCol(H, 'shoot');
        const cEdit = findCol(H, 'edit');
        const cChannel = findCol(H, 'channel');
        const cLink = findCol(H, 'link', 'url');
        const cArtist = findCol(H, 'artist');
        const cLoc = findCol(H, 'location');
        const cDress = findCol(H, 'dress');
        const g = (r, i) => i >= 0 ? String(r[i] ?? '').trim() : '';
        const rowsOut = [];
        for (let i = headerIdx + 1; i < data.length; i++) {
          const r = data[i];
          const title = g(r, cSong >= 0 ? cSong : 0);
          if (!title) continue;
          rowsOut.push({
            song_title: title,
            singer: g(r, cSinger),
            month: g(r, cMonth),
            video_shoot: g(r, cShoot),
            video_edit: g(r, cEdit),
            channel_name: g(r, cChannel),
            youtube_link: g(r, cLink),
            artist: g(r, cArtist),
            location: g(r, cLoc),
            dress_colour: g(r, cDress),
          });
        }
        if (!rowsOut.length) { message.error('No song rows found in Excel'); return; }
        const res = await api.post('/video-tracker/import-firsttime', { rows: rowsOut });
        message.success(`First Time Use: ${res.data.added} added (${res.data.skipped} skipped as duplicate)`);
        setTab('firsttime');
        load();
      } catch (err) {
        message.error(formatApiError(err, 'Import failed'));
      }
    };
    reader.readAsBinaryString(file);
    return false;
  };

  const transferToUploaded = async () => {
    if (!ftSelected.length) return;
    try {
      const res = await api.post('/video-tracker/transfer-uploaded', { ids: ftSelected });
      message.success(`${res.data.transferred} row(s) Uploaded me bhej diye`);
      setFtSelected([]);
      load();
    } catch (err) {
      message.error(formatApiError(err, 'Transfer failed'));
    }
  };

  // ---- Download the import template (same column order the importer reads) ----
  const downloadTemplate = () => {
    const aoa = [
      ['Song', 'Artist', 'Singer', 'Video Shoot', 'Dress Colour', 'Note'],
      ['Kabutar Bole Gutar Gu 012', 'Bainsla Music', 'DG Mauli', 'DJ Shoot', 'Red', ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 28 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Video Tracker');
    XLSX.writeFile(wb, 'video-tracker-format.xlsx');
  };

  const submitAdd = async () => {
    try {
      const vals = await addForm.validateFields();
      if (editRow) {
        await api.put(`/video-tracker/${editRow.id}`, vals);
        message.success('Updated');
      } else {
        const res = await api.post('/video-tracker', vals);
        const link = (vals.youtube_link || '').trim();
        if (link) {
          const ch = resolveChannel(vals.channel_name);
          const up = await api.post(`/video-tracker/${res.data.id}/upload`, {
            channel_id: ch.channel_id,
            channel_name: ch.channel_name,
            video_title: vals.video_title,
            youtube_link: link,
          });
          message.success(`Uploaded — code ${up.data?.first_time_code || ''} (First Time Use + Uploaded me aa gaya)`);
        } else {
          message.success('Song added to pending');
        }
      }
      setAddModal(false); setEditRow(null); addForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Save failed'));
    }
  };

  const addChannelLocal = (ch) => setChannels(prev => (prev.some(c => c.id === ch.id) ? prev : [ch, ...prev]));

  const openFtAdd = async () => {
    ftAddForm.resetFields();
    setFtAddModal(true);
    api.get('/video-tracker/cms-list').then(r => setCmsList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    try {
      const res = await api.get('/video-tracker/next-code');
      if (!ftAddForm.getFieldValue('first_time_code')) ftAddForm.setFieldsValue({ first_time_code: res.data.code });
    } catch { /* server assigns it on save */ }
  };

  // Resolve typed/selected channel name into { channel_id, channel_name }.
  const resolveChannel = (name) => {
    const nm = (name || '').trim();
    const ch = channels.find(c => (c.channel_name || '').trim().toLowerCase() === nm.toLowerCase());
    return { channel_id: ch ? ch.id : null, channel_name: nm };
  };

  const submitUpload = async () => {
    try {
      const vals = await uploadForm.validateFields();
      const ch = resolveChannel(vals.channel_name);
      const res = await api.post(`/video-tracker/${uploadRow.id}/upload`, {
        channel_id: ch.channel_id,
        channel_name: ch.channel_name,
        video_title: vals.video_title,
        youtube_link: vals.youtube_link,
      });
      message.success(`Uploaded — code ${res.data?.first_time_code || ''} (First Time Use me aa gaya)`);
      setUploadRow(null); uploadForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Failed'));
    }
  };

  const submitEditUpload = async () => {
    try {
      const vals = await editUploadForm.validateFields();
      const ch = resolveChannel(vals.channel_name);
      await api.put(`/video-tracker/${editUploadRow.id}`, {
        song_title: vals.song_title ?? editUploadRow.song_title,
        artist: vals.artist ?? editUploadRow.artist,
        singer: vals.singer ?? editUploadRow.singer,
        month: vals.month ?? editUploadRow.month,
        video_shoot: vals.video_shoot ?? editUploadRow.video_shoot,
        video_edit: vals.video_edit ?? editUploadRow.video_edit,
        location: vals.location ?? editUploadRow.location,
        dress_colour: vals.dress_colour ?? editUploadRow.dress_colour,
        channel_id: ch.channel_id,
        channel_name: ch.channel_name,
        video_title: vals.video_title,
        youtube_link: vals.youtube_link,
      });
      message.success('Updated');
      setEditUploadRow(null); editUploadForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Failed'));
    }
  };

  const openEditUpload = (row) => {
    setEditUploadRow(row);
    editUploadForm.setFieldsValue({
      song_title: row.song_title || '',
      singer: row.singer || '',
      artist: row.artist || '',
      month: row.month || '',
      video_shoot: row.video_shoot || '',
      video_edit: row.video_edit || '',
      location: row.location || '',
      dress_colour: row.dress_colour || '',
      channel_name: row.channel_name || '',
      video_title: row.video_title || '',
      youtube_link: row.youtube_link || '',
    });
  };

  const revert = async (id) => {
    try { await api.post(`/video-tracker/${id}/revert`); message.success('Moved back to pending'); load(); }
    catch (err) { message.error(formatApiError(err, 'Failed')); }
  };

  const del = async (id) => {
    try { await api.delete(`/video-tracker/${id}`); message.success('Deleted'); load(); }
    catch (err) { message.error(formatApiError(err, 'Failed')); }
  };

  const openNotify = async () => {
    try {
      const [uRes, nRes] = await Promise.all([
        api.get('/auth/users'),
        api.get('/video-tracker/notify-users'),
      ]);
      setStaff((uRes.data || []).filter(u => !['admin', 'super_admin'].includes(u.role)));
      notifyForm.setFieldsValue({ user_ids: nRes.data || [] });
      setNotifyModal(true);
    } catch (err) { message.error(formatApiError(err, 'Failed')); }
  };

  const saveNotify = async () => {
    try {
      const vals = await notifyForm.validateFields();
      await api.post('/video-tracker/notify-users', { user_ids: vals.user_ids || [] });
      message.success('Notification staff updated');
      setNotifyModal(false);
    } catch (err) { message.error(formatApiError(err, 'Failed')); }
  };

  const submitFtAdd = async () => {
    try {
      const vals = await ftAddForm.validateFields();
      const ch = resolveChannel(vals.channel_name);
      const res = await api.post('/video-tracker/add-firsttime', { ...vals, channel_id: ch.channel_id, channel_name: ch.channel_name });
      message.success(`First Time Use me add ho gaya: ${res.data.song_title} (${res.data.first_time_code}) — staff ko "Details bharo & Public karo" me dikhega`);
      setFtAddModal(false);
      ftAddForm.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Add failed'));
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    addForm.setFieldsValue(row);
    setAddModal(true);
  };

  const pendingCols = [
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', render: t => <b>{t}</b> },
    { title: 'Artist', dataIndex: 'artist' },
    { title: 'Singer', dataIndex: 'singer' },
    { title: 'Video Shoot', dataIndex: 'video_shoot' },
    { title: 'Dress Colour', dataIndex: 'dress_colour' },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 220,
      render: (_, row) => (
        <Space>
          <Button type="primary" size="small" icon={<YoutubeOutlined />} onClick={() => { setUploadRow(row); uploadForm.resetFields(); }}>
            Mark Uploaded
          </Button>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} /></Tooltip>
          <Popconfirm title="Delete this song?" onConfirm={() => del(row.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const uploadedCols = [
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', render: t => <b>{t}</b> },
    { title: 'Singer', dataIndex: 'singer' },
    { title: 'Channel', dataIndex: 'channel_name', render: c => c ? <Tag color="red">{c}</Tag> : '-' },
    { title: 'Video Title', dataIndex: 'video_title' },
    {
      title: 'Link', dataIndex: 'youtube_link', render: l => l
        ? <a href={l} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-'
    },
    {
      title: 'Uploaded', dataIndex: 'uploaded_at',
      render: d => d ? <Text type="secondary">{new Date(d).toLocaleDateString('en-IN')}</Text> : '-'
    },
    { title: 'By', dataIndex: 'uploaded_by_name', render: n => n || '-' },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 210,
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit channel / link / title">
            <Button size="small" type="primary" ghost icon={<EditOutlined />} onClick={() => openEditUpload(row)}>Edit</Button>
          </Tooltip>
          <Tooltip title="Move back to Pending">
            <Popconfirm title="Move back to pending?" onConfirm={() => revert(row.id)}>
              <Button size="small" icon={<RollbackOutlined />}>Undo</Button>
            </Popconfirm>
          </Tooltip>
          <Popconfirm title="Delete this record?" onConfirm={() => del(row.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Video List = exactly the Excel sheet columns, in the same order.
  const listCols = [
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', width: 300, fixed: 'left', render: t => <b>{t}</b> },
    { title: 'Artist', dataIndex: 'artist', width: 200 },
    { title: 'Singer', dataIndex: 'singer', width: 170 },
    { title: 'Video Shoot', dataIndex: 'video_shoot', width: 160 },
    { title: 'Dress Colur', dataIndex: 'dress_colour', width: 260 },
    { title: 'Note', dataIndex: 'note', width: 150 },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 110,
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit details"><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} /></Tooltip>
          <Popconfirm title="Delete this video record?" onConfirm={() => del(row.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const openCopy = async (row) => {
    setCopyRow(row); setCopyData(null);
    try { const res = await api.get(`/video-tracker/${row.id}/copy-check`); setCopyData(res.data); }
    catch (e) { message.error(formatApiError(e)); setCopyRow(null); }
  };
  const recheckCopy = async () => {
    try { await api.post(`/video-tracker/${copyRow.id}/copy-check`); message.success('Dobara check shuru — 2-5 minute me result aayega'); setCopyRow(null); load(); }
    catch (e) { message.error(formatApiError(e)); }
  };
  const openCms = async (row) => {
    setCmsRow(row); setCmsData(null);
    try { const res = await api.get(`/video-tracker/${row.id}/cms-status`); setCmsData(res.data || []); }
    catch (e) { message.error(formatApiError(e)); setCmsRow(null); }
  };
  const cmsUndo = async (chId) => {
    try { await api.post(`/video-tracker/${cmsRow.id}/cms-undo`, { channel_id: chId }); openCms(cmsRow); load(); }
    catch (e) { message.error(formatApiError(e)); }
  };
  const cmsTag = (r) => {
    if (!r.cms_scope) return '-';
    const done = Number(r.cms_done || 0), total = Number(r.cms_total || 0), wrong = Number(r.cms_wrong || 0);
    return (
      <span style={{ cursor: 'pointer' }} onClick={() => openCms(r)}>
        <Tag color="geekblue" style={{ fontWeight: 'bold' }}>Sirf {r.cms_scope}</Tag>
        <Tag color={total && done >= total ? 'green' : 'orange'}>{done}/{total} channel</Tag>
        {wrong > 0 && <Tag color="red" style={{ fontWeight: 'bold' }}>Galat channel: {wrong}</Tag>}
      </span>
    );
  };
  const copyTag = (r) => {
    const s = r.copy_status;
    if (!s) return '-';
    const t = {
      queued: ['blue', 'Check ho raha hai'], checking: ['blue', 'Check ho raha hai'],
      waiting: ['orange', 'Public hone ka intezaar'], fresh: ['green', 'Fresh'],
      copy: ['red', `COPY! (${r.copy_matches})`], maybe: ['gold', `Shayad copy (${r.copy_matches})`],
      error: ['default', 'Check nahi hua'], no_link: ['default', 'Link nahi hai'],
    }[s] || ['default', s];
    return <Tag color={t[0]} style={{ cursor: 'pointer', fontWeight: s === 'copy' ? 'bold' : undefined }} onClick={() => openCopy(r)}>{t[1]}</Tag>;
  };

  const firstTimeCols = [
    { title: 'Code', dataIndex: 'first_time_code', key: 'code', width: 80, fixed: 'left', render: c => c ? <Tag color="purple" style={{ fontWeight: 'bold', fontSize: 13 }}>{c}</Tag> : '-' },
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', width: 240, fixed: 'left', render: t => <b>{t}</b> },
    { title: 'Singer', dataIndex: 'singer', width: 150 },
    { title: 'Artist', dataIndex: 'artist', width: 150 },
    { title: 'Dress Colour', dataIndex: 'dress_colour', width: 160 },
    { title: 'Month', dataIndex: 'month', width: 90 },
    { title: 'Video Shoot', dataIndex: 'video_shoot', width: 120 },
    { title: 'Video Edit', dataIndex: 'video_edit', width: 120 },
    { title: 'Channel', dataIndex: 'channel_name', width: 170, render: c => c ? <Tag color="red">{c}</Tag> : '-' },
    {
      title: 'Link', dataIndex: 'youtube_link', width: 90, render: l => l
        ? <a href={l} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-'
    },
    { title: 'Copy Check', dataIndex: 'copy_status', width: 150, render: (_, r) => copyTag(r) },
    {
      title: 'CMS', dataIndex: 'cms_scope', width: 230, render: (_, r) => cmsTag(r),
      filters: [...new Set(rows.map(r => r.cms_scope).filter(Boolean))].map(v => ({ text: v, value: v })),
      onFilter: (v, r) => r.cms_scope === v
    },
    { title: 'Location', dataIndex: 'location', width: 140 },
    { title: 'By', dataIndex: 'uploaded_by_name', width: 120, render: n => n || '-' },
    {
      title: 'Public?', dataIndex: 'publish_status', width: 150,
      render: (v, r) => (
        <Space direction="vertical" size={2}>
          {v === 'pending' ? <Tag color="orange">Details pending</Tag> : v === 'public' ? <Tag color="green">Public</Tag> : '-'}
          {Number(r.shown_count) > 0
            ? <Tag color="blue">Staff: {r.done_count}/{r.shown_count} Done</Tag>
            : (v === 'pending' ? <Tag>Staff ko nahi dikhaya</Tag> : null)}
        </Space>
      )
    },
    {
      title: 'Action', key: 'action', fixed: 'right', width: 190,
      render: (_, row) => (
        <Space>
          {isAdmin && (
            <Tooltip title="Bainsla Music staff ko dikhao — wo details bharke Public karenge">
              <Button size="small" icon={<EyeOutlined />} onClick={() => openShowStaff(row)}>Staff ko dikhao</Button>
            </Tooltip>
          )}
          <Tooltip title="Edit channel / link / title">
            <Button size="small" type="primary" ghost icon={row.youtube_link ? <EditOutlined /> : <LinkOutlined />} onClick={() => openEditUpload(row)}>
              {row.youtube_link ? 'Edit' : 'Link Daalo'}
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Pending (Not Uploaded)" value={stats.pending} valueStyle={{ color: '#faad14' }} prefix={<UploadOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="First Time Use" value={stats.firsttime || 0} valueStyle={{ color: '#722ed1' }} prefix={<YoutubeOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Uploaded to YouTube" value={stats.uploaded} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Total Songs" value={stats.total} prefix={<YoutubeOutlined />} /></Card></Col>
      </Row>

      <Card
        title={<Title level={4} style={{ margin: 0 }}><YoutubeOutlined /> Video Upload Tracker</Title>}
        extra={
          <Space wrap>
            <Input
              allowClear placeholder="Search song / channel / singer" prefix={<SearchOutlined />}
              onChange={(e) => {
                const v = e.target.value;
                if (searchTimer.current) clearTimeout(searchTimer.current);
                searchTimer.current = setTimeout(() => setSearch(v), 400);
              }}
              style={{ width: 220 }}
            />
            <Select
              allowClear placeholder="Filter by channel" style={{ width: 180 }}
              value={channelFilter} onChange={setChannelFilter}
              showSearch optionFilterProp="children"
            >
              {channels.map(c => <Select.Option key={c.id} value={c.id}>{c.channel_name}</Select.Option>)}
            </Select>
            {isAdmin && <Button icon={<BellOutlined />} onClick={openNotify}>Notify Staff</Button>}
            <Button onClick={downloadTemplate}>Excel Format</Button>
            <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleImport}>
              <Button icon={<UploadOutlined />}>Import Pending</Button>
            </Upload>
            <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleImportFirstTime}>
              <Button icon={<UploadOutlined />} style={{ borderColor: '#722ed1', color: '#722ed1' }}>Import First Time Use</Button>
            </Upload>
            <Button icon={<PlusOutlined />} style={{ borderColor: '#722ed1', color: '#722ed1' }}
              onClick={openFtAdd}>Add First Time Use</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRow(null); addForm.resetFields(); setAddModal(true); }}>Add Video</Button>
          </Space>
        }
      >
        <Tabs
          activeKey={tab} onChange={setTab}
          items={[
            { key: 'pending', label: <Badge count={stats.pending} offset={[10, 0]} color="#faad14">Pending&nbsp;&nbsp;</Badge> },
            { key: 'firsttime', label: <Badge count={stats.firsttime || 0} offset={[10, 0]} color="#722ed1">First Time Use&nbsp;&nbsp;</Badge> },
            { key: 'uploaded', label: <Badge count={stats.uploaded} offset={[10, 0]} color="#52c41a">Uploaded&nbsp;&nbsp;</Badge> },
            { key: 'list', label: <Badge count={stats.all || 0} offset={[10, 0]} color="#1677ff">Video List&nbsp;&nbsp;</Badge> },
          ]}
        />
        {tab === 'firsttime' && (
          <Space style={{ marginBottom: 8 }} wrap>
            <Button icon={<PlusOutlined />} style={{ borderColor: '#722ed1', color: '#722ed1' }}
              onClick={openFtAdd}>Naya First Time Use (Channel + Link ke saath)</Button>
            <Button
              type="primary"
              disabled={!ftSelected.length}
              onClick={transferToUploaded}
            >
              Uploaded me bhejo{ftSelected.length ? ` (${ftSelected.length})` : ''}
            </Button>
            <span style={{ color: '#888' }}>Select karke Uploaded me bhejo — dono jagah rahega</span>
            <Button icon={<LinkOutlined />} style={{ borderColor: '#0f9d58', color: '#0f9d58' }} onClick={openFtSheet}>
              Google Sheet kholo
            </Button>
          </Space>
        )}
        {tab === 'list' && (
          <Space style={{ marginBottom: 8 }} wrap>
            <Text type="secondary">Excel sheet ki poori list — jaisi upload ki gayi thi.</Text>
          </Space>
        )}
        <Table
          rowKey="id" loading={loading}
          columns={tab === 'pending' ? pendingCols : tab === 'firsttime' ? firstTimeCols : tab === 'list' ? listCols : uploadedCols}
          dataSource={rows}
          rowSelection={tab === 'firsttime' ? { selectedRowKeys: ftSelected, onChange: setFtSelected } : undefined}
          scroll={{ x: tab === 'firsttime' ? 1700 : tab === 'list' ? 1350 : 900 }}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          size="small"
        />
      </Card>

      {/* Add / Edit song */}
      <Modal
        title={editRow ? 'Edit Video' : 'Add Video'} open={addModal}
        onOk={submitAdd} onCancel={() => { setAddModal(false); setEditRow(null); }}
        okText="Save" width={720}
      >
        <Form form={addForm} layout="vertical">
          <VtSongPicker form={addForm} open={addModal} label="Song / Number (Song Library se search karo)" />
          <Form.Item name="artist" label="Artist"><Input /></Form.Item>
          <Form.Item name="singer" label="Singer"><Input placeholder="e.g. DG Mauli" /></Form.Item>
          <Form.Item name="video_shoot" label="Video Shoot"><Input placeholder="e.g. Dj Shoot / Park Shoot" /></Form.Item>
          <Form.Item name="dress_colour" label="Dress Colur"><Input placeholder="e.g. Dark Blue Top & Lehanga" /></Form.Item>
          <Form.Item name="note" label="Note"><Input placeholder="e.g. Holi Song" /></Form.Item>
          {!editRow && (
            <>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Video YouTube par upload ho chuka hai? To Channel + YouTube Link bhi daalo — seedha Uploaded + First Time Use me jayega (BM code ke saath). Khali chhodo to Pending me rahega.
              </Text>
              <VtChannelField form={addForm} channels={channels} onAdded={addChannelLocal} dependencies={['youtube_link']}
                rules={[({ getFieldValue }) => ({
                  validator: (_, v) => (!(getFieldValue('youtube_link') || '').trim() || (v || '').trim()
                    ? Promise.resolve() : Promise.reject(new Error('Link daala hai to Channel bhi choose karo'))),
                })]} />
              <Form.Item name="youtube_link" label="YouTube Link"><Input placeholder="https://youtu.be/..." /></Form.Item>
              <Form.Item name="video_title" label="Video Title (optional)"><Input placeholder="Chaho to video title daalo" /></Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Add a single First-Time-Use record (all columns) */}
      <Modal
        title={<span><PlusOutlined /> Add First Time Use</span>}
        open={ftAddModal} onOk={submitFtAdd} onCancel={() => setFtAddModal(false)} okText="Save" width={720}
      >
        <Form form={ftAddForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}><Form.Item name="first_time_code" label="Code (agla BM number apne aap)"><Input placeholder="BM0001" /></Form.Item></Col>
          </Row>
          <VtSongPicker form={ftAddForm} open={ftAddModal} />
          <Row gutter={12}>
            <Col span={12}><Form.Item name="singer" label="Singer"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="artist" label="Artist"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="month" label="Month"><Input placeholder="e.g. Oct 2025" /></Form.Item></Col>
            <Col span={8}><Form.Item name="video_shoot" label="Video Shoot"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="video_edit" label="Video Edit"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <VtChannelField form={ftAddForm} channels={channels} onAdded={addChannelLocal} />
            </Col>
            <Col span={12}>
              <Form.Item name="youtube_link" label="YouTube Link"><Input placeholder="https://youtu.be/..." /></Form.Item>
            </Col>
          </Row>
          <Form.Item name="cms_scope" label="Sirf is CMS ke channels par daalna hai (optional)"
            extra="Chunoge to us CMS ke saare channels ke staff ke dashboard par card aayega: 'Ye gaana sirf is CMS ke channels par'. Har channel ka hisab tab tak dikhega jab tak sab channel par upload na ho jaye.">
            <Select allowClear placeholder={cmsList.length ? 'CMS chuno (e.g. WMG)' : 'Pehle Bainsla Music Channels me channels ka CMS bharo'}
              options={cmsList.map(c => ({ value: c.cms_name, label: `${c.cms_name} (${c.channels} channel)` }))} />
          </Form.Item>
          <Form.Item name="video_title" label="Video Title (optional)"><Input placeholder="Chaho to video title daalo" /></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="location" label="Location"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="dress_colour" label="Dress"><Input /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* Mark uploaded */}
      <Modal
        title={<span><YoutubeOutlined style={{ color: '#f00' }} /> Mark Uploaded — {uploadRow?.song_title}</span>}
        open={!!uploadRow} onOk={submitUpload} onCancel={() => setUploadRow(null)} okText="Mark Uploaded"
      >
        <Form form={uploadForm} layout="vertical">
          <VtChannelField form={uploadForm} channels={channels} onAdded={addChannelLocal} rules={[{ required: true, message: 'Channel choose or type karo' }]} />
          <Form.Item name="youtube_link" label="YouTube Link" rules={[{ required: true, message: 'Paste the YouTube link' }]}>
            <Input placeholder="https://youtube.com/watch?v=..." />
          </Form.Item>
          <Form.Item name="video_title" label="Video Title (optional)">
            <Input placeholder="Chaho to video title daalo" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit uploaded record (channel / link / title) — stays uploaded */}
      <Modal
        title={<span><EditOutlined /> Edit Upload — {editUploadRow?.song_title}</span>}
        open={!!editUploadRow} onOk={submitEditUpload} onCancel={() => setEditUploadRow(null)} okText="Save"
      >
        <Form form={editUploadForm} layout="vertical">
          <Form.Item name="song_title" label="Song / Title" rules={[{ required: true, message: 'Song title required' }]}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="singer" label="Singer"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="artist" label="Artist"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="month" label="Month"><Input placeholder="e.g. Oct" /></Form.Item></Col>
            <Col span={8}><Form.Item name="video_shoot" label="Video Shoot"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="video_edit" label="Video Edit"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="location" label="Location"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="dress_colour" label="Dress"><Input /></Form.Item></Col>
          </Row>
          <VtChannelField form={editUploadForm} channels={channels} onAdded={addChannelLocal} rules={[{ required: true, message: 'Channel choose or type karo' }]} />
          <Form.Item name="youtube_link" label="YouTube Link" rules={editUploadRow?.status === 'uploaded' ? [{ required: true, message: 'Paste the YouTube link' }] : []}>
            <Input placeholder="https://youtube.com/watch?v=..." />
          </Form.Item>
          <Form.Item name="video_title" label="Video Title (optional)">
            <Input placeholder="Chaho to video title daalo" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Notify staff (admin) */}
      <Modal
        title={<span><BellOutlined /> Staff who see new-upload notifications (7 days)</span>}
        open={notifyModal} onOk={saveNotify} onCancel={() => setNotifyModal(false)} okText="Save"
      >
        <Text type="secondary">Selected staff will see uploaded songs on their dashboard for 7 days.</Text>
        <Form form={notifyForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="user_ids" label="Select staff">
            <Select mode="multiple" showSearch optionFilterProp="children" placeholder="Choose staff">
              {staff.map(u => <Select.Option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={<span><EyeOutlined /> Staff ko dikhao — {showRow?.first_time_code} {showRow?.song_title}</span>}
        open={!!showRow} onOk={saveShowStaff} onCancel={() => setShowRow(null)} okText="Save" confirmLoading={showSaving}
      >
        <div style={{ marginBottom: 10, color: '#8c8c8c' }}>
          Jin staff ko tick karoge, unke dashboard par ye gaana "Naye song upload hue — details bharo" me dikhega, jab tak wo Done nahi dabate. Tick hatane se unse chhup jayega.
        </div>
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" onClick={() => setShowPicked(showStaff.map(u => Number(u.id)))}>Sab tick</Button>
          <Button size="small" onClick={() => setShowPicked([])}>Sab hatao</Button>
        </Space>
        <Checkbox.Group style={{ width: '100%' }} value={showPicked} onChange={vals => setShowPicked(vals)}>
          <Space direction="vertical">
            {showStaff.map(u => {
              const t = showTargets.find(x => Number(x.user_id) === Number(u.id) && Number(x.hidden) === 0);
              return (
                <Checkbox key={u.id} value={Number(u.id)}>
                  {u.full_name || u.username} <Text type="secondary" style={{ fontSize: 12 }}>({u.role})</Text>
                  {t && (t.done_at ? <Tag color="green" style={{ marginLeft: 6 }}>Done</Tag> : <Tag color="orange" style={{ marginLeft: 6 }}>Dikh raha hai</Tag>)}
                </Checkbox>
              );
            })}
          </Space>
        </Checkbox.Group>
        {!showStaff.length && <Text type="secondary">Bainsla Music company me koi staff nahi mila.</Text>}
      </Modal>
      <Modal
        title={<span>CMS hisab — {cmsRow?.first_time_code} {cmsRow?.song_title} <Tag color="geekblue">Sirf {cmsRow?.cms_scope}</Tag></span>}
        open={!!cmsRow} onCancel={() => setCmsRow(null)} width={760}
        footer={<Button type="primary" onClick={() => setCmsRow(null)}>Theek hai</Button>}
      >
        {!cmsData ? <Text type="secondary">Load ho raha hai...</Text> : (
          <div>
            {cmsRow?.youtube_link && /(?:v=|youtu\.be\/|shorts\/)([\w-]{11})/.test(cmsRow.youtube_link) && (
              <img alt="" style={{ width: 200, borderRadius: 6, marginBottom: 8 }}
                src={`https://i.ytimg.com/vi/${cmsRow.youtube_link.match(/(?:v=|youtu\.be\/|shorts\/)([\w-]{11})/)[1]}/mqdefault.jpg`} />
            )}
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary">Upload ho gaya: {cmsData.filter(c => +c.in_scope && c.done_at).length}/{cmsData.filter(c => +c.in_scope).length} · Hamare channels par same video aate hi apne aap ✅ lag jata hai (har 30 min check).</Text>
            </div>
            <Table size="small" rowKey="channel_id" pagination={false} dataSource={cmsData} scroll={{ x: 640 }}
              columns={[
                { title: 'Channel', dataIndex: 'channel_name', render: (n, c) => <span>{+c.in_scope ? <Tag color="red">{n}</Tag> : <Tag color="volcano" style={{ fontWeight: 'bold' }}>GALAT: {n} ({c.cms_name || 'no CMS'})</Tag>}</span> },
                { title: 'Staff', dataIndex: 'staff_names', render: s => s || <Text type="secondary">koi assign nahi</Text> },
                { title: 'Status', render: (_, c) => c.done_at
                  ? <Space size={4} wrap><Tag color={+c.in_scope ? 'green' : 'red'}>{+c.in_scope ? 'Upload ho gaya' : 'Galat channel par upload'}</Tag>
                      {c.link && <a href={c.link} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a>}
                      <Text type="secondary" style={{ fontSize: 12 }}>{String(c.done_at).slice(0, 10)} {+c.auto ? '(auto)' : (c.done_by_name || '')}</Text></Space>
                  : <Tag color="orange">Baaki</Tag> },
                { title: '', width: 70, render: (_, c) => +c.in_scope && c.done_at ? <Popconfirm title="Wapas Baaki karein?" onConfirm={() => cmsUndo(c.channel_id)}><Button size="small" icon={<RollbackOutlined />} /></Popconfirm> : null },
              ]} />
          </div>
        )}
      </Modal>
      <Modal
        title={<span><YoutubeOutlined style={{ color: '#ff0000' }} /> Copy Check — {copyRow?.first_time_code} {copyRow?.song_title}</span>}
        open={!!copyRow} onCancel={() => setCopyRow(null)} width={720}
        footer={<Space>
          {copyData && <Button onClick={recheckCopy}>Dobara check karo</Button>}
          <Button type="primary" onClick={() => setCopyRow(null)}>Theek hai</Button>
        </Space>}
      >
        {!copyData ? <Text type="secondary">Load ho raha hai...</Text> : (
          <div>
            <div style={{ marginBottom: 12 }}>
              {copyTag({ copy_status: copyData.status, copy_matches: copyData.match_count })}
              <Text type="secondary" style={{ marginLeft: 6 }}>
                {copyData.status === 'fresh' && 'Hamare channels par ye video pehle se nahi mila. '}
                {copyData.status === 'copy' && 'Ye video (same frames) hamare channel par pehle se upload hai. '}
                {copyData.status === 'maybe' && 'Ek frame / thumbnail milta hai — ek baar khud dekh lo. '}
                {copyData.note}
                {copyData.checked_at && ` · Checked: ${copyData.checked_at}`}
              </Text>
            </div>
            {(copyData.matches || []).map(mt => (
              <div key={mt.video_id} style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                <a href={`https://youtu.be/${mt.video_id}`} target="_blank" rel="noreferrer">
                  <img src={`https://i.ytimg.com/vi/${mt.video_id}/mqdefault.jpg`} alt="" style={{ width: 160, borderRadius: 6 }} />
                </a>
                <div style={{ minWidth: 0 }}>
                  <div><Tag color={mt.level === 'copy' ? 'red' : 'gold'}>{mt.frames}/{mt.of} frame same{mt.thumb_same ? ' + thumbnail same' : ''}</Tag>
                    {mt.channel && <Tag color="red">{mt.channel}</Tag>}</div>
                  <div><b>{mt.title}</b></div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {mt.published_at ? `Upload: ${String(mt.published_at).slice(0, 10)} · ` : ''}
                    <a href={`https://youtu.be/${mt.video_id}`} target="_blank" rel="noreferrer"><LinkOutlined /> youtu.be/{mt.video_id}</a>
                  </Text>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
